/* eslint-disable @typescript-eslint/no-explicit-any */
import { ElementRef, ReactNode, forwardRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { CheckIcon } from '../Icons';

import { Color, cn } from 'styles';
import { ChangeEventLike, buildChangeEventLike } from './types';

const colorClasses = {
  primary:
    'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
  secondary:
    'data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground data-[state=checked]:border-secondary',
  success:
    'data-[state=checked]:bg-success data-[state=checked]:text-success-foreground data-[state=checked]:border-success',
  destructive:
    'data-[state=checked]:bg-destructive data-[state=checked]:text-destructive-foreground data-[state=checked]:border-destructive',
  warning:
    'data-[state=checked]:bg-warning data-[state=checked]:text-warning-foreground data-[state=checked]:border-warning',

  info: 'data-[state=checked]:bg-info data-[state=checked]:text-info-foreground data-[state=checked]:border-info',
};

export const checkboxSizeConfig = {
  small: {
    sizeClass: 'h-4 w-4',
    iconSize: 12,
  },

  medium: {
    sizeClass: 'h-5 w-5',
    iconSize: 14,
  },

  large: {
    sizeClass: 'h-6 w-6',
    iconSize: 16,
  },
};

type CheckboxSize = keyof typeof checkboxSizeConfig;

export interface CheckboxProps {
  className?: string;
  checked?: boolean;
  defaultChecked?: boolean;

  color?: Extract<Color, 'primary' | 'secondary' | 'success' | 'destructive' | 'info' | 'warning'>;

  size?: CheckboxSize;

  children?: ReactNode;
  icon?: ReactNode;
  iconSize?: number;

  disabled?: boolean;
  name?: string;
  id?: string;
  onChangeDirect?: (newChecked: boolean) => any;
  onChange?: (eventLike: ChangeEventLike<boolean>) => any;
}

const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  (
    {
      className,
      onChangeDirect,
      onChange,
      color = 'primary',
      children,
      iconSize,
      icon,
      size = 'medium',
      ...props
    },
    ref,
  ) => {
    const handleChange = (value: boolean): void => {
      onChangeDirect?.(value);
      onChange?.(
        buildChangeEventLike({
          name: props.name,
          id: props.id,
          value,
          checked: value,
        }),
      );
    };

    const sizeConfig = checkboxSizeConfig[size];

    const hasChildren = !!children;

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        {...props}
        onCheckedChange={handleChange}
        className={cn(
          'peer shrink-0',
          'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          !hasChildren && [
            'rounded-sm border disabled:cursor-not-allowed disabled:opacity-50',
            colorClasses[color],
            sizeConfig.sizeClass,
          ],
          className,
        )}
      >
        {children || (
          <CheckboxPrimitive.Indicator
            className={cn('flex items-center justify-center text-current')}
          >
            {icon || <CheckIcon size={iconSize || sizeConfig.iconSize} />}
          </CheckboxPrimitive.Indicator>
        )}
      </CheckboxPrimitive.Root>
    );
  },
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
